import { ServiceHandler, applyQueryConfig } from '../utils/api.utils';
import { standardQuerySerializer } from '../api/serializers/query.serializers';
import { formsServiceMappings } from '../console/support/formsLibrary/formsServiceMappings';
import { commonDateParamMappings } from './common/mappings';

const getDocumentsMappings = [
  ...commonDateParamMappings,
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  {
    path: 'docType',
    transform: ({ docType = [] }) => {
      return docType?.join(', ');
    },
  },
  {
    path: 'agencyId',
  },
  {
    path: 'submissionId',
    paramKey: 'submissionId.keyword',
  },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      switch (orderBy) {
        case 'uploadTime':
          return `${orderBy}.timestamp,${order}`;
        case 'systemGenerated':
        case 'docTime':
        case 'modified':
          return `${orderBy},${order}`;
        default:
          return orderBy ? `${orderBy}.keyword,${order}` : null;
      }
    },
  },
];

const getDocumentsHandler = ServiceHandler({
  url: '/api/docs/v1/search/:accountId',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(getDocumentsMappings, params)),
});
export const getDocuments = (params: any = {}) =>
  getDocumentsHandler(params.accountId, { params });

const getPHDocumentsMappings = [
  ...commonDateParamMappings,
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  { path: 'docType' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      switch (orderBy) {
        case 'uploadTime':
          return `${orderBy}.timestamp,${order}`;
        case 'systemGenerated':
        case 'modified':
          return `${orderBy},${order}`;
        default:
          return orderBy ? `${orderBy}.keyword,${order}` : null;
      }
    },
  },
];

const getPHDocumentsHandler = ServiceHandler({
  url: '/api/docs/v1/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(getPHDocumentsMappings, params)),
});
export const getPHDocuments = (params: Record<string, unknown> = {}) =>
  getPHDocumentsHandler({ params });

const getDocumentsByAgencyIdMappings = [
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      switch (orderBy) {
        case 'uploadTime':
          return `${orderBy}.timestamp,${order}`;
        case 'systemGenerated':
        case 'modified':
          return `${orderBy},${order}`;
        default:
          return orderBy ? `${orderBy}.keyword,${order}` : null;
      }
    },
  },
];

const getDocumentsByAgencyIdHandler = ServiceHandler({
  url: '/api/docs/v1/search/:agencyId',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(getDocumentsByAgencyIdMappings, params)
    ),
});
export const getDocumentsByAgencyId = (params: any = {}) =>
  getDocumentsByAgencyIdHandler(params.agencyId, { params });

const uploadDocumentHandler = ServiceHandler({
  url: '/api/docs/v1/upload/doc',
  method: 'post',
});
export function uploadDocument(
  data: any,
  accountId: any,
  docType: any,
  docName: any,
  temp: any,
  agencyId: any
) {
  return uploadDocumentHandler({
    params: { accountId, docType, docName, temp, agencyId },
    data,
  });
}

const uploadDocumentV2Handler = ServiceHandler({
  url: `/api/docs/v1/upload/doc`,
  method: 'post',
});
export const uploadDocumentV2 = (
  params: Record<string, unknown> = {},
  data?: unknown
) => uploadDocumentV2Handler({ params, data });

const updateNoticeHandler = ServiceHandler({
  url: ' /api/account/v1/agency/surplus/notice/upload',
  method: 'post',
});
export const updateNotice = (
  params: Record<string, unknown> = {},
  data?: unknown
) => updateNoticeHandler({ params, data });

const updateDocumentHandler = ServiceHandler({
  url: '/api/docs/v1/update',
  method: 'put',
});
export const updateDocument = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateDocumentHandler({ data });

const downloadDocumentHandler = ServiceHandler({
  url: '/api/docs/v1/download?docId=:docId',
});
export const downloadDocument = ({ docId }: { docId: string | number }) =>
  downloadDocumentHandler(docId);

const downloadBullpenDocumentHandler = ServiceHandler({
  url: '/api/claimsmgmt/v1/claimsFile/download?claimsFileId=:claimsFileId',
});
export const downloadBullpenDocument = ({
  claimsFileId,
}: {
  claimsFileId: string | number;
}) => downloadBullpenDocumentHandler(claimsFileId);

const deleteDocumentByIdHandler = ServiceHandler({
  url: '/api/docs/v1?docId=:docId',
  method: 'delete',
});
export const deleteDocumentById = ({ docId }: { docId: string | number }) =>
  deleteDocumentByIdHandler(docId);

const getDocTypesHandler = ServiceHandler({
  url: '/api/docs/v1/docTypes',
});
export const getDocTypes = (params: Record<string, unknown> = {}) =>
  getDocTypesHandler({ params });

const uploadNOCHandler = ServiceHandler({
  url: 'api/policy/v1/noc/:policyId',
  method: 'put',
});
export const uploadNOC = (params: any = {}, data?: unknown) =>
  uploadNOCHandler(params.policyId, { data });

// ==========================================
//           Forms Library Services
// ==========================================

const getFormsHandler = ServiceHandler({
  url: '/api/docs/v1/forms/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(formsServiceMappings, params)),
});
export const getForms = (params: Record<string, unknown> = {}) =>
  getFormsHandler({ params });

const getFormByIdHandler = ServiceHandler({ url: '/api/docs/v1/forms/id/:id' });
export const getFormById = ({ id }: { id: string | number }) =>
  getFormByIdHandler(id);

const getAllFormsHandler = ServiceHandler({
  url: '/api/docs/v1/forms/search?size=2000',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(formsServiceMappings, params)),
});
export const getAllForms = (params: Record<string, unknown> = {}) =>
  getAllFormsHandler({ params });

const createFormTemplateHandler = ServiceHandler({
  url: '/api/docs/v1/forms',
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
});
export const createFormTemplate = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createFormTemplateHandler({ data });

const deleteFormHandler = ServiceHandler({
  url: '/api/docs/v1/forms/:id',
  method: 'DELETE',
});
export const deleteForm = ({ id }: { id: string | number }) =>
  deleteFormHandler(id);

const updateFormTemplateHandler = ServiceHandler({
  url: 'api/docs/v1/forms/:id',
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
});
export const updateFormTemplate = (
  { id }: { id: string | number },
  data?: unknown
) => updateFormTemplateHandler(id, { data });

const formAddDocInfoHandler = ServiceHandler({
  url: '/api/docs/v1/forms/doc/:id',
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
});
export const formAddDocInfo = (
  { id }: { id: string | number },
  data?: unknown
) => formAddDocInfoHandler(id, { data });

export const getSupportedPaperTypes = ServiceHandler({
  url: '/api/docs/v1/forms/papertypes',
});

export const getProductTypes = ServiceHandler({
  url: '/api/docs/v1/forms/producttypes',
});

export const getFormTypes = ServiceHandler({
  url: '/api/docs/v1/formtype/search',
});

const createFormTypeHandler = ServiceHandler({
  url: '/api/docs/v1/formtype',
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
});
export const createFormType = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createFormTypeHandler({ data });

const updateFormTypeHandler = ServiceHandler({
  url: '/api/docs/v1/formtype/:id',
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
});
export const updateFormType = (
  { id }: { id: string | number },
  data?: unknown
) => updateFormTypeHandler(id, { data });

const deleteFormTypeHandler = ServiceHandler({
  url: '/api/docs/v1/formtype/:id',
  method: 'DELETE',
});
export const deleteFormType = ({ id }: { id: string | number }) =>
  deleteFormTypeHandler(id);
